import React, { useState, useEffect } from 'react';
import { Star, Calendar, Activity, Award, BookOpen, TrendingUp, Sparkles, MapPin, Grid, Atom, Users, Brain, Download } from 'lucide-react';
import PlanetTable from '../components/PlanetTable';
import VargaChartTable from '../components/VargaChartTable';
import DashaTimeline from '../components/DashaTimeline';
import DashaSystemsComplete from '../components/DashaSystemsComplete';
import VarsaphalaDisplay from '../components/VarsaphalaDisplay';
import VarsaphalaYearByYear from '../components/VarsaphalaYearByYear';
import TransitDisplay from '../components/TransitDisplay';
import BhavaTable from '../components/BhavaTable';
import AshtakavargaTable, { AllPlanetsBavGrid, SavDisplay } from '../components/AshtakavargaTable';
import AshtakavargaHouseWise from '../components/AshtakavargaHouseWise';
import AshtakavargaMatrixDisplay from '../components/AshtakavargaMatrixDisplay';
import CharaKarakaDisplay from '../components/CharaKarakaDisplay';
import ArudhaPadasDisplay from '../components/ArudhaPadasDisplay';
import UpagrahasDisplay from '../components/UpagrahasDisplay';
import YogiDagdhaDisplay from '../components/YogiDagdhaDisplay';
import SpecialLagnasDisplay from '../components/SpecialLagnasDisplay';
import ShadbalaComplete from '../components/ShadbalaComplete';
import BhavaBalaComplete from '../components/BhavaBalaComplete';
import AyurdayaDisplay from '../components/AyurdayaDisplay';
import BulkUpload from '../components/BulkUpload';
import LLMTesting from '../components/LLMTesting';

// Dashboard Card Component
const DashboardCard = ({ id, title, icon: Icon, children, expanded, onToggle }) => (
  <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
    <div 
      className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5 transition-colors"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-cyan-400" />
        <h3 className="text-white text-lg font-medium">{title}</h3>
      </div>
      <svg 
        className={`w-5 h-5 text-cyan-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
    {expanded && (
      <div className="p-6 border-t border-white/10">
        {children}
      </div>
    )}
  </div>
);

const KernelDisplayComplete = () => {
  const [mode, setMode] = useState('single'); // 'single', 'bulk', or 'llm'
  
  const [formData, setFormData] = useState({
    name: 'Mandeep',
    gender: 'Male',
    date: '1997-05-14',
    time: '12:17',
    location: 'Dehradun, Uttarakhand, India',
    latitude: 30.3255646,
    longitude: 78.0436813,
    timezone: 'Asia/Kolkata'
  });

  const [kernelData, setKernelData] = useState(null);
  const [bulkKernels, setBulkKernels] = useState([]); // Store bulk uploaded kernels
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      
      console.log('Submitting to:', `${BACKEND_URL}/api/kernel/generate`);
      console.log('Request data:', {
        person_id: formData.name,
        full_name: formData.name,
        gender: formData.gender,
        date: formData.date,
        time: formData.time + ':00',
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        timezone: formData.timezone,
        place_name: formData.location
      });
      
      const response = await fetch(`${BACKEND_URL}/api/kernel/generate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          person_id: formData.name,
          full_name: formData.name,
          gender: formData.gender,
          date: formData.date,
          time: formData.time + ':00',
          latitude: parseFloat(formData.latitude),
          longitude: parseFloat(formData.longitude),
          timezone: formData.timezone,
          place_name: formData.location
        })
      });

      console.log('Response status:', response.status);

      // Check if response is ok BEFORE reading body
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      // Parse JSON response (only once!)
      const data = await response.json();
      
      console.log('Kernel response data:', data);
      
      if (data.status === 'success' && data.kernel) {
        console.log('✅ Kernel data received successfully');
        setKernelData(data.kernel);
        
        // Store user ID for relationship kernel (use returned user_id from backend)
        const userId = data.user_id || 
                       data.kernel.person_id || 
                       data.kernel['1.1_birth_astronomical_data']?.person_id || 
                       formData.name;
        localStorage.setItem('currentKernelUserId', userId);
        console.log('📝 Stored user ID for relationship kernel:', userId);
        
        setShowVideo(true);
      } else {
        console.error('Invalid response structure:', data);
        setError(data.message || 'Failed to generate kernel data');
      }
    } catch (err) {
      console.error('Kernel generation error:', err);
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Location autocomplete
  const searchLocation = async (query) => {
    if (query.length < 3) {
      setLocationSuggestions([]);
      return;
    }

    setIsSearchingLocation(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`
      );
      const data = await response.json();
      setLocationSuggestions(data);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Location search error:', error);
    } finally {
      setIsSearchingLocation(false);
    }
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      location: value
    });
    searchLocation(value);
  };

  const selectLocation = (location) => {
    const displayName = location.display_name;
    setFormData({
      ...formData,
      location: displayName,
      latitude: parseFloat(location.lat),
      longitude: parseFloat(location.lon)
    });
    setShowSuggestions(false);
    setLocationSuggestions([]);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadAARated = async () => {
    try {
      setIsDownloading(true);
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      console.log('Starting download from:', `${BACKEND_URL}/api/download/aa-rated-people`);
      
      const response = await fetch(`${BACKEND_URL}/api/download/aa-rated-people`);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      console.log('Blob size:', blob.size);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AA_rated_people_birth_details.json';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setIsDownloading(false);
      }, 1000);
      
      console.log('✅ Download initiated successfully');
      alert('✅ File downloaded successfully! Check your Downloads folder.');
      
    } catch (error) {
      console.error('❌ Download error:', error);
      setIsDownloading(false);
      alert(`Failed to download file: ${error.message}`);
    }
  };

  // Form Screen
  if (!showVideo && !kernelData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-white text-4xl font-light mb-2">🔮 Complete Astrology Kernel</h1>
            <p className="text-cyan-400 text-lg">Get Your Full Vedic Chart with All Details</p>
            
            {/* Download AA-Rated People Button */}
            <div className="mt-6">
              <button
                onClick={handleDownloadAARated}
                disabled={isDownloading}
                className={`inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-medium rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105 ${isDownloading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Download className={`w-5 h-5 ${isDownloading ? 'animate-bounce' : ''}`} />
                {isDownloading ? 'Downloading...' : 'Download AA-Rated People List (159 People)'}
              </button>
              <p className="text-white/60 text-sm mt-2">Complete birth details for validation testing</p>
            </div>
          </div>

          {/* Mode Toggle Tabs */}
          <div className="flex justify-center mb-8">
            <div className="bg-black/30 backdrop-blur-xl rounded-2xl p-1 border border-white/20 inline-flex">
              <button
                onClick={() => setMode('single')}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  mode === 'single'
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <Star className="w-5 h-5" />
                Single Chart
              </button>
              <button
                onClick={() => setMode('bulk')}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  mode === 'bulk'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <Users className="w-5 h-5" />
                Bulk Upload
              </button>
              <button
                onClick={() => setMode('llm')}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  mode === 'llm'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                    : 'text-white/60 hover:text-white'
                }`}
              >
                <Brain className="w-5 h-5" />
                LLM Testing
              </button>
            </div>
          </div>

          {/* Content Based on Mode */}
          {mode === 'single' ? (
            <div className="bg-black/30 backdrop-blur-xl rounded-3xl p-8 max-w-2xl w-full border border-white/20 mx-auto">

          <form onSubmit={handleSubmit} className="space-y-4">
            <input 
              type="text" 
              name="name" 
              placeholder="Full Name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full p-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50"
              required
            />
            
            <div className="grid grid-cols-2 gap-4">
              <select 
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="p-3 rounded-xl bg-white/10 border border-white/20 text-white"
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              
              <input 
                type="date" 
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className="p-3 rounded-xl bg-white/10 border border-white/20 text-white"
                required
              />
            </div>

            <input 
              type="time" 
              name="time"
              value={formData.time}
              onChange={handleInputChange}
              className="w-full p-3 rounded-xl bg-white/10 border border-white/20 text-white"
              required
            />

            <div className="relative">
              <input 
                type="text" 
                name="location"
                placeholder="Birth Place"
                value={formData.location}
                onChange={handleLocationChange}
                className="w-full p-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50"
                required
              />
              {isSearchingLocation && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-400"></div>
                </div>
              )}
              {showSuggestions && locationSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {locationSuggestions.map((location, index) => (
                    <div
                      key={index}
                      className="p-3 hover:bg-gray-700 cursor-pointer text-white border-b border-gray-700 last:border-b-0"
                      onClick={() => selectLocation(location)}
                    >
                      <div className="font-medium">{location.display_name}</div>
                      <div className="text-sm text-gray-400">
                        Lat: {parseFloat(location.lat).toFixed(4)}, Lon: {parseFloat(location.lon).toFixed(4)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button 
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium hover:from-cyan-600 hover:to-blue-600 transition-all disabled:opacity-50"
            >
              {loading ? 'Calculating...' : 'Get Complete Chart'}
            </button>

            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3 text-red-300">
                {error}
              </div>
            )}
          </form>
        </div>
          ) : mode === 'bulk' ? (
            <div className="bg-black/30 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
              <BulkUpload onKernelsLoaded={setBulkKernels} />
            </div>
          ) : (
            <div className="bg-black/30 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
              <LLMTesting kernelData={kernelData} bulkKernels={bulkKernels} />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Video Screen
  if (showVideo && kernelData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center p-4">
        <div className="text-center max-w-2xl">
          <h2 className="text-white text-3xl font-light mb-6">Sage Vaarta</h2>
          <video 
            className="rounded-2xl mb-6"
            controls
            autoPlay
            src="/sage-vaarta.mp4"
          />
          <button
            onClick={() => setShowVideo(false)}
            className="px-8 py-3 rounded-xl bg-cyan-500 text-white font-medium hover:bg-cyan-600 transition-colors"
          >
            Continue to Complete Chart →
          </button>
        </div>
      </div>
    );
  }

  // Download relationship kernel handler (single file)
  const handleDownloadRelationshipKernel = async () => {
    if (!kernelData) {
      alert('Please generate a kernel first');
      return;
    }

    const userId = kernelData.person_id || 
                   kernelData['1.1_birth_astronomical_data']?.person_id || 
                   localStorage.getItem('currentKernelUserId') || 
                   'user_kernel';

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/kernel/relationship/${userId}/download`;
    
    try {
      console.log('📥 Downloading relationship kernel for:', userId);
      
      const response = await fetch(url);
      
      console.log('📥 Response received, status:', response.status);
      
      // CRITICAL: Check response status BEFORE reading body
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      // Now read the response body ONCE
      const blob = await response.blob();
      
      console.log('📦 Blob created, size:', blob.size);
      
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${userId}_relationship_kernel.json`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      console.log('✅ Relationship kernel downloaded successfully');
      
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(`Failed to download relationship kernel: ${error.message}`);
    }
  };

  // Download relationship kernel handler (split into 4 files as ZIP)
  const handleDownloadRelationshipKernelSplit = async () => {
    if (!kernelData) {
      alert('Please generate a kernel first');
      return;
    }

    const userId = kernelData.person_id || 
                   kernelData['1.1_birth_astronomical_data']?.person_id || 
                   localStorage.getItem('currentKernelUserId') || 
                   'user_kernel';

    const url = `${process.env.REACT_APP_BACKEND_URL}/api/kernel/relationship/${userId}/download-split`;
    
    try {
      console.log('📥 Downloading split relationship kernel (4 files) for:', userId);
      
      const response = await fetch(url);
      
      console.log('📥 Response received, status:', response.status);
      
      // CRITICAL: Check response status BEFORE reading body
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
      
      // Now read the response body ONCE
      const blob = await response.blob();
      
      console.log('📦 ZIP created, size:', blob.size);
      
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${userId}_relationship_kernel_split.zip`;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      
      // Cleanup after a short delay
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }, 100);
      
      console.log('✅ Split relationship kernel (ZIP with 4 files) downloaded successfully');
      
    } catch (error) {
      console.error('❌ Download error:', error);
      alert(`Failed to download split relationship kernel: ${error.message}`);
    }
  };

  // Download handler
  const handleDownload = async (format) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      
      // Show loading indicator
      console.log(`Downloading ${format} format...`);
      
      const response = await fetch(`${backendUrl}/api/download/kernel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          person_id: kernelData.input?.person_id || kernelData.input?.full_name || 'user',
          kernel_data: kernelData,
          format: format
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download error response:', errorText);
        throw new Error(`Download failed: ${response.status}`);
      }

      // Get filename from Content-Disposition header or create default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `vedic_chart_${new Date().getTime()}.${format}`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 100);
      
      console.log(`✓ Downloaded: ${filename}`);
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${format.toUpperCase()}: ${error.message}`);
    }
  };

  // Main Data Display
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-white text-4xl font-light mb-2">{kernelData.input?.full_name}</h1>
          <p className="text-cyan-400 text-xl">{kernelData.input?.date} • {kernelData.input?.time}</p>
          <p className="text-white/60">{kernelData.input?.place}</p>
          
          {/* Download Buttons */}
          <div className="text-center mt-6">
            <p className="text-cyan-400 text-sm mb-3">💡 Best for LLM: <strong>Optimized</strong> (all sections, perfect size) | <strong>Relationship (4 files)</strong> - split for easier handling</p>
            <div className="flex flex-wrap gap-3 justify-center">
              {/* BEST FOR LLM */}
              <button
                onClick={() => handleDownload('json_optimized')}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold hover:from-yellow-600 hover:to-orange-600 transition-all flex items-center gap-2 shadow-lg border-2 border-yellow-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
                JSON Optimized ⭐
              </button>
              
              {/* NEW: RELATIONSHIP KERNEL - Direct Download */}
              <button
                onClick={handleDownloadRelationshipKernel}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold hover:from-pink-600 hover:to-purple-600 transition-all flex items-center gap-2 shadow-lg border-2 border-pink-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                🔱 Relationship Kernel (1 file)
              </button>
              
              {/* NEW: RELATIONSHIP KERNEL SPLIT - Download as 4 files in ZIP */}
              <button
                onClick={handleDownloadRelationshipKernelSplit}
                className="px-6 py-3 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold hover:from-purple-600 hover:to-indigo-600 transition-all flex items-center gap-2 shadow-lg border-2 border-purple-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" />
                </svg>
                🔱 Relationship (4 files ZIP)
              </button>
            </div>
            
            <p className="text-gray-400 text-xs mt-3 mb-2">Other options:</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => handleDownload('json_compact')}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium hover:from-green-700 hover:to-emerald-700 transition-all flex items-center gap-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                JSON (Compact)
              </button>
              <button
                onClick={() => handleDownload('markdown_compact')}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium hover:from-blue-700 hover:to-cyan-700 transition-all flex items-center gap-2 shadow-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Markdown (Compact)
              </button>
              <button
                onClick={() => handleDownload('xml')}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:from-purple-600 hover:to-pink-600 transition-all flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                XML (Full)
              </button>
              <button
                onClick={() => handleDownload('json')}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 text-white font-medium hover:from-green-600 hover:to-emerald-600 transition-all flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                JSON (Full)
              </button>
              <button
                onClick={() => handleDownload('markdown')}
                className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium hover:from-blue-600 hover:to-cyan-600 transition-all flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                </svg>
                Markdown (Full)
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {/* Section 1.1: Birth Astronomical Data */}
          <DashboardCard
            id="birth_astro"
            title="Section 1.1: Birth Astronomical Data"
            icon={Star}
            expanded={expandedSections.birth_astro}
            onToggle={() => toggleSection('birth_astro')}
          >
            {kernelData["1.1_birth_astronomical_data"] && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <InfoBox label="Date of Birth" value={kernelData["1.1_birth_astronomical_data"].dob} />
                  <InfoBox label="Time of Birth" value={kernelData["1.1_birth_astronomical_data"].tob} />
                  <InfoBox label="Place" value={kernelData["1.1_birth_astronomical_data"].place} />
                  <InfoBox label="Timezone" value={kernelData["1.1_birth_astronomical_data"].tz} />
                  <InfoBox label="Ayanamsha" value={kernelData["1.1_birth_astronomical_data"].ayanamsha?.system} />
                  <InfoBox label="Ayanamsha Value" value={kernelData["1.1_birth_astronomical_data"].ayanamsha?.dms} />
                </div>
                
                {kernelData["1.1_birth_astronomical_data"].panchanga && (
                  <div className="mt-6">
                    <h4 className="text-yellow-400 text-lg mb-3">Panchanga</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      <InfoBox label="Tithi" value={kernelData["1.1_birth_astronomical_data"].panchanga.tithi_name} />
                      <InfoBox label="Vara (Weekday)" value={kernelData["1.1_birth_astronomical_data"].panchanga.vara?.weekday} />
                      <InfoBox label="Nakshatra" value={kernelData["1.1_birth_astronomical_data"].panchanga.nakshatra} />
                      <InfoBox label="Pada" value={kernelData["1.1_birth_astronomical_data"].panchanga.nakshatra_pada} />
                      <InfoBox label="Yoga" value={kernelData["1.1_birth_astronomical_data"].panchanga.yoga?.yoga_name} />
                      <InfoBox label="Karana" value={kernelData["1.1_birth_astronomical_data"].panchanga.karana} />
                    </div>
                  </div>
                )}
              </div>
            )}
          </DashboardCard>

          {/* Section 1.2: D1 Rasi Chart */}
          <DashboardCard
            id="d1_rasi"
            title="Section 1.2: D1 Rasi Chart (Main Birth Chart)"
            icon={Star}
            expanded={expandedSections.d1_rasi}
            onToggle={() => toggleSection('d1_rasi')}
          >
            {kernelData["1.2_d1_rasi_per_planet"]?.all_rows && (
              <PlanetTable 
                planets={kernelData["1.2_d1_rasi_per_planet"].all_rows}
                title="Complete D1 Chart - All Bodies (Lagna + Planets + Outer Planets)"
              />
            )}
          </DashboardCard>

          {/* Section 2: All Varga Charts D2-D60 */}
          <DashboardCard
            id="vargas"
            title="Section 2: All Divisional Charts (D2 to D60)"
            icon={Activity}
            expanded={expandedSections.vargas}
            onToggle={() => toggleSection('vargas')}
          >
            {kernelData.section_2_varga_system?.["2.1_varga_grids"] && (
              <div className="space-y-8">
                {Object.entries(kernelData.section_2_varga_system["2.1_varga_grids"]).map(([vargaName, vargaData]) => (
                  <VargaChartTable 
                    key={vargaName}
                    vargaData={vargaData}
                    chartName={vargaName.replace('_', ' ')}
                  />
                ))}
              </div>
            )}
          </DashboardCard>

          {/* Section 3: Bhava Chalit */}
          <DashboardCard
            id="bhava"
            title="Section 3: Bhava Chalit (House Cusps)"
            icon={MapPin}
            expanded={expandedSections.bhava}
            onToggle={() => toggleSection('bhava')}
          >
            {kernelData.section_3_bhava_chalit?.["3.1_bhava_cusps"] && (
              <BhavaTable bhavaData={kernelData.section_3_bhava_chalit["3.1_bhava_cusps"]} />
            )}
            
            {/* 3.2 Planet Bhava Positions */}
            {kernelData.section_3_bhava_chalit?.["3.2_planet_bhava_positions"] && (
              <div className="mt-8">
                <h4 className="text-cyan-400 text-lg mb-4">Planet Positions in Bhava Houses</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-purple-500/30">
                        <th className="text-left p-3 text-teal-300">Planet</th>
                        <th className="text-left p-3 text-teal-300">Longitude</th>
                        <th className="text-left p-3 text-teal-300">Sign</th>
                        <th className="text-left p-3 text-teal-300">Degree in Sign</th>
                        <th className="text-left p-3 text-teal-300">Bhava House</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kernelData.section_3_bhava_chalit["3.2_planet_bhava_positions"].map((planet, idx) => (
                        <tr key={idx} className="border-b border-purple-500/20 hover:bg-purple-500/10">
                          <td className="p-3 text-yellow-400 font-medium">{planet.planet}</td>
                          <td className="p-3 text-white/80">{planet.longitude.toFixed(2)}°</td>
                          <td className="p-3 text-white/80">{planet.sign}</td>
                          <td className="p-3 text-white/80">{planet.degree_in_sign.toFixed(2)}°</td>
                          <td className="p-3 text-cyan-400 font-medium">House {planet.bhava_house}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DashboardCard>

          {/* Section 5: Jaimini Chara Karakas */}
          <DashboardCard
            id="jaimini"
            title="Section 5: Jaimini Chara Karakas (Seven Significators)"
            icon={Atom}
            expanded={expandedSections.jaimini}
            onToggle={() => toggleSection('jaimini')}
          >
            {kernelData.section_5_jaimini && (
              <CharaKarakaDisplay jaiminiData={kernelData.section_5_jaimini} />
            )}
          </DashboardCard>

          {/* Section 5.3: All Arudha Padas */}
          <DashboardCard
            id="arudhas"
            title="Section 5.3: All Ārūḍha Padas (A1-A12)"
            icon={Atom}
            expanded={expandedSections.arudhas}
            onToggle={() => toggleSection('arudhas')}
          >
            {kernelData.section_5_jaimini?.["5.3_arudhas_all"] && (
              <ArudhaPadasDisplay arudhasData={kernelData.section_5_jaimini["5.3_arudhas_all"]} />
            )}
          </DashboardCard>

          {/* Section 6A: Upagrahas & Sphutas */}
          <DashboardCard
            id="upagrahas"
            title="Section 6: Upagrahas & Sphutas (Shadow Points)"
            icon={Activity}
            expanded={expandedSections.upagrahas}
            onToggle={() => toggleSection('upagrahas')}
          >
            {kernelData.section_6_upagrahas_sphutas && (
              <UpagrahasDisplay upagrahasData={kernelData.section_6_upagrahas_sphutas} />
            )}
          </DashboardCard>

          {/* Section 6B: Yogi/Dagdha/Trimurti */}
          <DashboardCard
            id="yogi"
            title="Section 6: Yogi/Avayogi, Dagdha & Trimūrti"
            icon={Star}
            expanded={expandedSections.yogi}
            onToggle={() => toggleSection('yogi')}
          >
            {kernelData.section_6_yogi_dagdha_trimurti && (
              <YogiDagdhaDisplay yogiData={kernelData.section_6_yogi_dagdha_trimurti} />
            )}
          </DashboardCard>

          {/* Section 7: Special Lagnas with GL+HL */}
          <DashboardCard
            id="special_lagnas"
            title="Section 7: Special Lagnas & GL+HL Wealth Metric"
            icon={Sparkles}
            expanded={expandedSections.special_lagnas}
            onToggle={() => toggleSection('special_lagnas')}
          >
            {kernelData.section_7_special_lagnas_points && (
              <SpecialLagnasDisplay specialLagnasData={kernelData.section_7_special_lagnas_points} />
            )}
          </DashboardCard>

          {/* Section 9: Strength Systems (Complete Shadbala & Bhava Bala) */}
          <DashboardCard
            id="strength_systems"
            title="Section 9: Complete Strength Systems (Shadbala & Bhava Bala)"
            icon={Activity}
            expanded={expandedSections.strength_systems}
            onToggle={() => toggleSection('strength_systems')}
          >
            {kernelData.section_9_strength_systems && (
              <div className="space-y-8">
                {/* Shadbala */}
                {kernelData.section_9_strength_systems['9.1_shadbala_complete'] && (
                  <div>
                    <ShadbalaComplete data={kernelData.section_9_strength_systems['9.1_shadbala_complete']} />
                  </div>
                )}
                
                {/* Bhava Bala */}
                {kernelData.section_9_strength_systems['9.2_bhava_bala_complete'] && (
                  <div className="mt-12">
                    <BhavaBalaComplete data={kernelData.section_9_strength_systems['9.2_bhava_bala_complete']} />
                  </div>
                )}
                
                {/* Ayurdaya */}
                {kernelData.section_9_strength_systems['9.3_ayurdaya_longevity'] && (
                  <div className="mt-12">
                    <AyurdayaDisplay data={kernelData.section_9_strength_systems['9.3_ayurdaya_longevity']} />
                  </div>
                )}
              </div>
            )}
          </DashboardCard>

          {/* Section 11: Complete Dasha Systems Catalogue */}
          <DashboardCard
            id="dashas"
            title="Section 11: Complete Dasha Systems (9 Major Systems)"
            icon={Calendar}
            expanded={expandedSections.dashas}
            onToggle={() => toggleSection('dashas')}
          >
            {kernelData.section_11_dasha_catalogue && (
              <DashaSystemsComplete data={kernelData.section_11_dasha_catalogue} />
            )}
          </DashboardCard>

          {/* Section 12: Varsaphala & Transit */}
          {kernelData.section_12_varsaphala_transit && (
            <>
              <DashboardCard
                id="varsaphala"
                title="Section 12.1: Varsaphala - Year by Year (Like Parashara Light)"
                icon={Calendar}
                expanded={expandedSections.varsaphala}
                onToggle={() => toggleSection('varsaphala')}
              >
                {kernelData.section_12_varsaphala_transit?.['12.1_varsaphala'] ? (
                  (() => {
                    // Extract natal data for year selector
                    const birthData = kernelData.section_1_birth_data?.['1.1_birth_astronomical_data'];
                    const d1Planets = kernelData.section_1_birth_data?.['1.2_d1_planetary_positions'];
                    const sunData = d1Planets?.find(p => p.planet === 'Sun');
                    const lagnaData = d1Planets?.find(p => p.planet === 'Lagna');
                    
                    const natalData = {
                      person_id: formData?.name || 'temp',
                      birth_date: birthData?.date_ymd || formData?.date || '1997-05-14',
                      sun_lon: sunData?.sidereal_longitude || 0,
                      asc_sign_idx: lagnaData?.sign_index || 0,
                      latitude: birthData?.latitude || formData?.latitude || 0,
                      longitude: birthData?.longitude || formData?.longitude || 0
                    };
                    
                    return (
                      <VarsaphalaDisplay 
                        data={kernelData.section_12_varsaphala_transit['12.1_varsaphala']} 
                        natalData={natalData}
                      />
                    );
                  })()
                ) : (
                  <div className="p-4 text-gray-600">Loading Varsaphala data...</div>
                )}
              </DashboardCard>

              <DashboardCard
                id="transit"
                title="Section 12.2: Current Transits (Gochar)"
                icon={Calendar}
                expanded={expandedSections.transit}
                onToggle={() => toggleSection('transit')}
              >
                {kernelData.section_12_varsaphala_transit['12.2_transit'] && (
                  <TransitDisplay data={kernelData.section_12_varsaphala_transit['12.2_transit']} />
                )}
              </DashboardCard>
            </>
          )}

          {/* Section 10: KP System */}
          <DashboardCard
            id="kp"
            title="Section 10: KP System (Krishnamurti Paddhati) - Placidus Cusps"
            icon={BookOpen}
            expanded={expandedSections.kp}
            onToggle={() => toggleSection('kp')}
          >
            <div className="space-y-8">
              {/* Planetary KP Table */}
              {kernelData.section_10_kp_system?.["10.2_planets_kp"] && (
                <div>
                  <h3 className="text-xl font-semibold text-cyan-400 mb-3">
                    KP Planetary Positions
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border-b-2 border-cyan-500/50">
                          <th className="text-left p-3 text-cyan-300 font-semibold">Planet</th>
                          <th className="text-left p-3 text-orange-300 font-semibold">R/C</th>
                          <th className="text-left p-3 text-cyan-300 font-semibold">Sign</th>
                          <th className="text-left p-3 text-yellow-300 font-semibold">Degrees</th>
                          <th className="text-left p-3 text-purple-300 font-semibold">Nakshatra</th>
                          <th className="text-left p-3 text-pink-300 font-semibold">Pada</th>
                          <th className="text-left p-3 text-green-300 font-semibold">RL</th>
                          <th className="text-left p-3 text-emerald-300 font-semibold">NL</th>
                          <th className="text-left p-3 text-blue-400 font-semibold">SL</th>
                          <th className="text-left p-3 text-violet-300 font-semibold">SS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kernelData.section_10_kp_system["10.2_planets_kp"].map((planet, idx) => {
                          const degInSign = Math.floor(planet.deg_in_sign);
                          const minDecimal = (planet.deg_in_sign - degInSign) * 60;
                          const minInSign = Math.floor(minDecimal);
                          const secInSign = Math.floor((minDecimal - minInSign) * 60);
                          const degreeFormat = `${String(degInSign).padStart(2, '0')}:${String(minInSign).padStart(2, '0')}:${String(secInSign).padStart(2, '0')}`;
                          
                          return (
                            <tr key={idx} className="border-b border-white/10 hover:bg-gradient-to-r hover:from-cyan-900/20 hover:to-blue-900/20 transition-colors">
                              <td className="p-3 text-cyan-400 font-bold">{planet.planet}</td>
                              <td className="p-3 text-orange-300 font-semibold text-center">{planet.rc_status || '-'}</td>
                              <td className="p-3 text-cyan-300 font-medium">{planet.sign}</td>
                              <td className="p-3 text-yellow-300 font-mono">{degreeFormat}</td>
                              <td className="p-3 text-purple-300">{planet.nakshatra}</td>
                              <td className="p-3 text-pink-300 text-center font-semibold">{planet.pada || '-'}</td>
                              <td className="p-3 text-green-300">{planet.sign_lord || '-'}</td>
                              <td className="p-3 text-emerald-300">{planet.star_lord}</td>
                              <td className="p-3 text-blue-400 font-bold">{planet.sub_lord}</td>
                              <td className="p-3 text-violet-300">{planet.sub_sub_lord || '-'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Cuspal KP Table */}
              {kernelData.section_10_kp_system?.["10.1_cusps_kp"] && (
                <div>
                  <h3 className="text-xl font-semibold text-indigo-400 mb-3">
                    KP House Cusps (Placidus System)
                  </h3>
                  <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gradient-to-r from-indigo-900/40 to-purple-900/40 border-b-2 border-indigo-500/50">
                      <th className="text-left p-3 text-indigo-300 font-semibold">House Cusp</th>
                      <th className="text-left p-3 text-cyan-300 font-semibold">Sign</th>
                      <th className="text-left p-3 text-yellow-300 font-semibold">Degree</th>
                      <th className="text-left p-3 text-purple-300 font-semibold">Nakshatra</th>
                      <th className="text-left p-3 text-pink-300 font-semibold">Pada</th>
                      <th className="text-left p-3 text-green-300 font-semibold">RL</th>
                      <th className="text-left p-3 text-emerald-300 font-semibold">NL</th>
                      <th className="text-left p-3 text-blue-400 font-semibold">SL</th>
                      <th className="text-left p-3 text-violet-300 font-semibold">SS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(kernelData.section_10_kp_system["10.1_cusps_kp"])
                      .sort((a, b) => {
                        const numA = parseInt(a[0].replace(/\D/g, ''));
                        const numB = parseInt(b[0].replace(/\D/g, ''));
                        return numA - numB;
                      })
                      .map(([cuspName, cusp], idx) => {
                        const signLords = {
                          "Aries": "Mars", "Taurus": "Venus", "Gemini": "Mercury", "Cancer": "Moon",
                          "Leo": "Sun", "Virgo": "Mercury", "Libra": "Venus", "Scorpio": "Mars",
                          "Sagittarius": "Jupiter", "Capricorn": "Saturn", "Aquarius": "Saturn", "Pisces": "Jupiter"
                        };
                        const signLord = signLords[cusp.sign] || "-";
                        
                        // Format as degrees:minutes:seconds
                        const degInSign = Math.floor(cusp.deg_in_sign);
                        const minDecimal = (cusp.deg_in_sign - degInSign) * 60;
                        const minInSign = Math.floor(minDecimal);
                        const secInSign = Math.floor((minDecimal - minInSign) * 60);
                        const degreeFormat = `${String(degInSign).padStart(2, '0')}:${String(minInSign).padStart(2, '0')}:${String(secInSign).padStart(2, '0')}`;
                        
                        const cuspNum = parseInt(cuspName.replace(/\D/g, ''));
                        const cuspLabel = cuspNum === 1 ? "1 (Asc)" : 
                                        cuspNum === 4 ? "4 (IC)" :
                                        cuspNum === 7 ? "7 (Desc)" :
                                        cuspNum === 10 ? "10 (MC)" : cuspNum.toString();
                        
                        return (
                          <tr key={idx} className="border-b border-white/10 hover:bg-gradient-to-r hover:from-indigo-900/20 hover:to-purple-900/20 transition-colors">
                            <td className="p-3 text-indigo-400 font-bold text-lg">{cuspLabel}</td>
                            <td className="p-3 text-cyan-300 font-medium">{cusp.sign}</td>
                            <td className="p-3 text-yellow-300 font-mono">{degreeFormat}</td>
                            <td className="p-3 text-purple-300">{cusp.nakshatra}</td>
                            <td className="p-3 text-pink-300 text-center font-semibold">{cusp.pada || '-'}</td>
                            <td className="p-3 text-green-300">{signLord}</td>
                            <td className="p-3 text-emerald-300">{cusp.star_lord}</td>
                            <td className="p-3 text-blue-400 font-bold">{cusp.sub_lord}</td>
                            <td className="p-3 text-violet-300">{cusp.sub_sub_lord || '-'}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
                  </div>
                  
                  {/* Legend */}
                  <div className="mt-4 p-4 bg-gray-800/30 rounded-lg border border-gray-700/50">
                    <p className="text-sm text-gray-300 mb-2"><span className="font-semibold text-indigo-400">Legend:</span></p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
                      <div><span className="text-orange-300 font-semibold">R/C</span> = Retrograde/Combust</div>
                      <div><span className="text-green-300 font-semibold">RL</span> = Rashi Lord (Sign Lord)</div>
                      <div><span className="text-emerald-300 font-semibold">NL</span> = Nakshatra Lord (Star Lord)</div>
                      <div><span className="text-blue-400 font-semibold">SL</span> = Sub Lord</div>
                      <div><span className="text-violet-300 font-semibold">SS</span> = Sub-Sub Lord</div>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">
                      ✨ Using KP Ayanamsa & Placidus House System for precise calculations
                    </p>
                  </div>
                </div>
              )}
            </div>
          </DashboardCard>

          {/* Section 13: Ashtakavarga */}
          <DashboardCard
            id="ashtakavarga"
            title="Section 13: Ashtakavarga (Bindu Points System)"
            icon={Grid}
            expanded={expandedSections.ashtakavarga}
            onToggle={() => toggleSection('ashtakavarga')}
          >
            {kernelData.add_on_13_samudaya_ashtakavarga && (
              <AshtakavargaMatrixDisplay 
                ashtakavargaData={kernelData.add_on_13_samudaya_ashtakavarga}
                d1Data={kernelData['1.2_d1_rasi_per_planet']}
              />
            )}

            {/* Fallback for old sign-wise display */}
            {kernelData.add_on_13_samudaya_ashtakavarga && !kernelData['1.2_d1_rasi_per_planet']?.lagna && (
              <div className="space-y-8">
                <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 text-yellow-200">
                  <p>Note: Lagna sign not available. Displaying sign-wise Ashtakavarga instead of house-wise.</p>
                </div>

                {/* Sarvashtakavarga (SAV) - Total of all planets */}
                {kernelData.add_on_13_samudaya_ashtakavarga["13.2_sav_sarvashtakavarga"] && (
                  <div>
                    <h3 className="text-yellow-400 text-2xl mb-4 font-medium">
                      Sarvashtakavarga (SAV) - Total Ashtakavarga
                    </h3>
                    <SavDisplay savData={kernelData.add_on_13_samudaya_ashtakavarga["13.2_sav_sarvashtakavarga"]} />
                  </div>
                )}

                {/* BAV Grid - All Planets */}
                {kernelData.add_on_13_samudaya_ashtakavarga["13.1_bav_per_planet"] && (
                  <div>
                    <h3 className="text-cyan-400 text-2xl mb-4 font-medium">
                      Bhinnaashtakavarga (BAV) - Per Planet Grid
                    </h3>
                    <div className="bg-gradient-to-br from-slate-900/50 to-purple-900/30 rounded-xl p-6 border border-purple-500/30">
                      <AllPlanetsBavGrid bavAllData={kernelData.add_on_13_samudaya_ashtakavarga["13.1_bav_per_planet"]} />
                    </div>
                  </div>
                )}

                {/* Individual Planet BAV Tables */}
                <div>
                  <h3 className="text-purple-400 text-2xl mb-4 font-medium">
                    Individual Planet Ashtakavarga
                  </h3>
                  <div className="grid gap-6">
                    {['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].map(planet => (
                      kernelData.add_on_13_samudaya_ashtakavarga["13.1_bav_per_planet"]?.[planet] && (
                        <AshtakavargaTable 
                          key={planet}
                          bavData={kernelData.add_on_13_samudaya_ashtakavarga["13.1_bav_per_planet"][planet]}
                          title={`${planet} Ashtakavarga`}
                        />
                      )
                    ))}
                  </div>
                </div>

                {/* Lagna Ashtakavarga */}
                {kernelData.add_on_13_samudaya_ashtakavarga["13.3_lagna_ashtakavarga"] && (
                  <div>
                    <h3 className="text-green-400 text-2xl mb-4 font-medium">
                      Lagna Ashtakavarga (LAV)
                    </h3>
                    <AshtakavargaTable 
                      bavData={kernelData.add_on_13_samudaya_ashtakavarga["13.3_lagna_ashtakavarga"]}
                      title="Lagna Ashtakavarga"
                    />
                  </div>
                )}

                {/* Rahu Ashtakavarga */}
                {kernelData.add_on_13_samudaya_ashtakavarga["13.4_rahu_ashtakavarga"] && (
                  <div>
                    <h3 className="text-orange-400 text-2xl mb-4 font-medium">
                      Rahu Ashtakavarga (Optional)
                    </h3>
                    <AshtakavargaTable 
                      bavData={kernelData.add_on_13_samudaya_ashtakavarga["13.4_rahu_ashtakavarga"]}
                      title="Rahu Ashtakavarga"
                    />
                    <p className="text-white/60 text-sm mt-2">
                      Note: Rahu BAV is separate from the classical SAV=337 total
                    </p>
                  </div>
                )}
              </div>
            )}
          </DashboardCard>

          {/* LLM Testing Section */}
          <DashboardCard
            id="llm_testing"
            title="🤖 AI Prediction Testing"
            icon={Brain}
            expanded={expandedSections.llm_testing}
            onToggle={() => toggleSection('llm_testing')}
          >
            <LLMTesting kernelData={kernelData} bulkKernels={bulkKernels} />
          </DashboardCard>

          {/* Add more sections here following the same pattern */}
        </div>
      </div>
    </div>
  );
};

// Helper component for info boxes
const InfoBox = ({ label, value }) => (
  <div className="bg-white/5 rounded-lg p-3 border border-white/10">
    <p className="text-white/60 text-xs mb-1">{label}</p>
    <p className="text-white font-medium">{value || 'N/A'}</p>
  </div>
);

export default KernelDisplayComplete;
